/**
 * Pagina de carrito de Ritualic
 *
 * La estructura viene del carrito de Not So Basic (`nsb-cart-page.liquid`):
 * franja de urgencia, dos columnas, notas del pedido, barra de envio gratis y
 * resumen con el boton de pago. Dos cosas cambian a proposito.
 *
 * 1. NSB recarga la pagina entera en cada clic de cantidad
 *    (`location.reload()`). Aqui se actualiza en sitio: se pide `/cart.js`, se
 *    repintan precio de linea, subtotal, ahorro, contador y barra de envio, y
 *    la pagina no parpadea. En un carrito se cambian cantidades varias veces
 *    seguidas y cada recarga es una oportunidad de abandonar.
 *
 * 2. Las cifras que cambian ruedan en vez de saltar. La tecnica es la del
 *    componente "Number Flow" de 21st.dev: dos capas superpuestas por ranura,
 *    la vieja sale y la nueva entra, y la direccion depende de si el numero
 *    sube o baja. Aqui se aplica a la cifra entera y no digito a digito, que
 *    con precios de seis cifras se lee mejor.
 */
/**
 * El contador del header lo lleva `cart-icon.js` de Horizon, que escucha el
 * evento estandar de Shopify pero espera su clase de evento, no un CustomEvent
 * cualquiera. En vez de imitar esa forma interna, se escribe directamente en la
 * burbuja: es un numero y una clase, y no depende de detalles del tema que
 * pueden cambiar en la proxima version.
 */
function ritEscribirBurbuja(cantidad) {
  document.querySelectorAll('.cart-bubble').forEach((burbuja) => {
    burbuja.classList.toggle('visually-hidden', cantidad === 0);
    const cifra = burbuja.querySelector('[ref="cartBubbleCount"]');
    if (!cifra) return;
    cifra.classList.toggle('hidden', cantidad === 0);
    cifra.textContent = cantidad < 100 ? String(cantidad) : '';
  });
}

/* Misma reafirmacion que en el cajon: Horizon repinta el header con datos
   viejos y a veces lo hace despues de nosotros. */
function ritActualizarBurbuja(cantidad) {
  ritEscribirBurbuja(cantidad);
  requestAnimationFrame(() => ritEscribirBurbuja(cantidad));
  setTimeout(() => ritEscribirBurbuja(cantidad), 500);
}

class RitCarrito extends HTMLElement {
  connectedCallback() {
    this.formato = this.dataset.formatoMoneda || '${{amount_no_decimals_with_comma_separator}}';
    this.umbral = Number(this.dataset.umbral || 0);

    this.addEventListener('click', (e) => {
      const paso = e.target.closest('[data-paso]');
      if (paso) return this.cambiar(paso.dataset.clave, Number(paso.dataset.cantidad) + Number(paso.dataset.paso));

      const quitar = e.target.closest('[data-quitar]');
      if (quitar) return this.cambiar(quitar.dataset.quitar, 0);
    });

    const notas = this.querySelector('[data-rit-carrito="notas"]');
    notas?.addEventListener('blur', () => this.guardarNota(true));

    // La nota se guarda antes de salir: si el cliente escribe y pulsa pagar sin
    // quitar el foco del campo, el `blur` no llega a tiempo.
    this.querySelector('[data-rit-carrito="pagar"]')?.addEventListener('click', (e) => {
      if (!notas || notas.value === notas.defaultValue) return;
      e.preventDefault();
      const destino = e.currentTarget.getAttribute('href');
      this.guardarNota(false).then(() => {
        window.location.href = destino;
      });
    });
  }

  ocupado(si) {
    this.classList.toggle('esta-ocupado', si);
  }

  dinero(centavos) {
    const valor = centavos / 100;
    const conSeparadores = (n, decimales, miles, decimal) => {
      const [entero, resto] = n.toFixed(decimales).split('.');
      const agrupado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
      return resto ? agrupado + decimal + resto : agrupado;
    };
    const piezas = {
      amount: () => conSeparadores(valor, 2, ',', '.'),
      amount_no_decimals: () => conSeparadores(valor, 0, ',', '.'),
      amount_with_comma_separator: () => conSeparadores(valor, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => conSeparadores(valor, 0, '.', ','),
      amount_with_space_separator: () => conSeparadores(valor, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => conSeparadores(valor, 0, ' ', ','),
      amount_with_apostrophe_separator: () => conSeparadores(valor, 2, "'", '.'),
    };
    const texto = this.formato.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, clave) =>
      piezas[clave] ? piezas[clave]() : conSeparadores(valor, 0, '.', ',')
    );
    return texto === this.formato ? `$${conSeparadores(valor, 0, '.', ',')}` : texto;
  }

  /**
   * Cambia el texto haciendolo rodar. La capa vieja sale y una copia nueva
   * entra, las dos en la misma direccion, que sale de si el numero sube o baja.
   */
  rodar(elemento, textoNuevo, sube) {
    if (!elemento || elemento.textContent.trim() === textoNuevo) return;

    const anterior = elemento.cloneNode(true);
    anterior.classList.add('rit-rueda__saliente');
    anterior.classList.toggle('hacia-arriba', sube);
    elemento.parentElement.append(anterior);

    elemento.textContent = textoNuevo;
    elemento.classList.remove('rit-rueda__entrante', 'hacia-arriba');
    void elemento.offsetWidth;
    elemento.classList.add('rit-rueda__entrante');
    elemento.classList.toggle('hacia-arriba', sube);

    anterior.addEventListener('animationend', () => anterior.remove(), { once: true });
    elemento.addEventListener(
      'animationend',
      () => elemento.classList.remove('rit-rueda__entrante', 'hacia-arriba'),
      { once: true }
    );
  }

  pintar(carrito) {
    const antes = Number(this.dataset.total || 0);
    const sube = carrito.total_price > antes;
    this.dataset.total = carrito.total_price;

    if (!carrito.item_count) {
      window.location.reload();
      return;
    }

    carrito.items.forEach((a) => {
      const fila = this.querySelector(`[data-linea="${CSS.escape(a.key)}"]`);
      if (!fila) return;
      this.rodar(fila.querySelector('[data-linea-precio]'), this.dinero(a.final_line_price), sube);
      const num = fila.querySelector('[data-linea-cantidad]');
      this.rodar(num, String(a.quantity), sube);
      fila.querySelectorAll('[data-paso]').forEach((b) => {
        b.dataset.cantidad = a.quantity;
      });
    });

    // Lineas que ya no estan en el carrito.
    this.querySelectorAll('[data-linea]').forEach((fila) => {
      if (!carrito.items.some((a) => a.key === fila.dataset.linea)) {
        fila.classList.add('se-va');
        fila.addEventListener('transitionend', () => fila.remove(), { once: true });
      }
    });

    this.rodar(this.querySelector('[data-rit-carrito="subtotal"]'), this.dinero(carrito.total_price), sube);

    const ahorro = this.querySelector('[data-rit-carrito="ahorro"]');
    if (ahorro) {
      // `/cart.js` no trae el precio de comparacion, asi que cada linea lo lleva
      // escrito en `data-compara` desde Liquid.
      const original = carrito.items.reduce((t, a) => {
        const fila = this.querySelector(`[data-linea="${CSS.escape(a.key)}"]`);
        const unidad = Number(fila?.dataset.compara || 0) || a.final_line_price / a.quantity;
        return t + unidad * a.quantity;
      }, 0);
      const dif = original - carrito.total_price;
      ahorro.closest('[data-rit-carrito="ahorro-fila"]').hidden = dif <= 0;
      if (dif > 0) this.rodar(ahorro, `− ${this.dinero(dif)}`, sube);
    }

    const cuenta = this.querySelector('[data-rit-carrito="cuenta"]');
    if (cuenta) this.rodar(cuenta, String(carrito.item_count), sube);
    const palabra = this.querySelector('[data-rit-carrito="cuenta-palabra"]');
    if (palabra) {
      palabra.textContent = carrito.item_count === 1 ? this.dataset.cuentaUno : this.dataset.cuentaVarios;
    }

    this.barraEnvio(carrito.total_price);

    ritActualizarBurbuja(carrito.item_count);

    // Que el icono del header y el carrito lateral se enteren.
    // Aqui NO se vuelve a despachar `shopify:cart:lines-update`. El contador de
    // Horizon escucha ese evento y suma por su cuenta a partir de las lineas que
    // trae, asi que un evento nuestro encima del valor que acabamos de escribir
    // lo hacia contar dos veces: el carrito tenia 1 articulo y la burbuja decia
    // 2. Un contador que miente en el header es el mismo problema de confianza
    // que el cajon vacio, mas pequeno.
  }

  barraEnvio(total) {
    if (this.umbral <= 0) return;
    const relleno = this.querySelector('[data-rit-carrito="envio-relleno"]');
    const texto = this.querySelector('[data-rit-carrito="envio-texto"]');
    const falta = this.umbral - total;
    if (relleno) relleno.style.width = `${Math.min(100, (total / this.umbral) * 100)}%`;
    if (texto) {
      texto.innerHTML =
        falta <= 0
          ? this.dataset.envioLogrado
          : this.dataset.envioFalta.replace('[[falta]]', `<strong>${this.dinero(falta)}</strong>`);
    }
  }

  async cambiar(clave, cantidad) {
    this.ocupado(true);
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: clave, quantity: Math.max(0, cantidad) }),
      });
      this.pintar(await r.json());
    } finally {
      this.ocupado(false);
    }
  }

  async guardarNota(avisar) {
    const ta = this.querySelector('[data-rit-carrito="notas"]');
    if (!ta) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: ta.value }),
    });
    ta.defaultValue = ta.value;
    if (!avisar) return;
    const aviso = this.querySelector('[data-rit-carrito="nota-guardada"]');
    if (!aviso) return;
    aviso.classList.add('se-ve');
    setTimeout(() => aviso.classList.remove('se-ve'), 2500);
  }
}

if (!customElements.get('rit-carrito')) customElements.define('rit-carrito', RitCarrito);
